class Dashboard {
  static EL_CONTAINER = '#dashboard-container';
  static PREFS_URL = '/api/dashboard/tile-prefs';

  constructor () {
    // Initialize dashboard with Muuri grid layout
    this.initMuuriGrid();
    // Customize panel (toggle tiles) + drag-order persistence
    this.initCustomize();
    this.initOrderPersistence();
  }

  /**
   * Initialize the Muuri grid layout for the dashboard
   */
  initMuuriGrid () {
    console.log('Initializing Muuri grid for dashboard');

    const gridContainer = document.querySelector('.dashboard-grid');
    if (!gridContainer) {
      console.error('Grid container not found');
      return;
    }

    // Initialize Muuri with options
    this.grid = new Muuri('.dashboard-grid', {
      items: '.grid-item',
      layoutOnInit: true,
      layout: {
        fillGaps: true,
        horizontal: false,
        alignRight: false,
        alignBottom: false,
        rounding: false,
      },
      dragEnabled: true,
      dragSortHeuristics: {
        sortInterval: 50,
        minDragDistance: 10,
        minBounceBackAngle: 1,
      },
      dragAutoScroll: {
        targets: [window],
        threshold: 50,
        safeZone: 0.2,
        speed: Muuri.AutoScroller.getDefaultSpeed,
        sortDuringScroll: true,
        smoothStop: false,
      },
      dragStartPredicate: {
        handle: '.card-header',
      },
      // Bind the Dragger (and its dragCssProps: user-select none etc.)
      // to the header only — without this Muuri applies user-select:none
      // to the WHOLE tile, making tile content (e.g. Data Chat answers)
      // unselectable/uncopyable.
      dragHandle: '.card-header',
      sortData: {
        position: function(item, element) {
          return parseFloat(element.getAttribute('data-position')) || 0;
        },
      },
    });

    // Sort grid items by their position attribute
    this.grid.sort('position', { layout: 'instant' });

    // Improve grid layout
    this.optimizeGridLayout();

    // Set up event listeners
    window.addEventListener('resize', this.handleResize.bind(this));
    window.addEventListener('load', this.handleContentLoaded.bind(this));
  }

  /**
   * Optimize the grid layout
   */
  optimizeGridLayout () {
    if (!this.grid) return;

    // Force a layout refresh
    this.grid.refreshItems().layout();

    // Process charts and ensure they're responsive
    const chartContainers = document.querySelectorAll('.chart-container');
    chartContainers.forEach(container => {
      container.style.maxWidth = '100%';
    });

    // Make all cards have consistent heights based on content
    const gridItems = document.querySelectorAll('.grid-item');
    gridItems.forEach(item => {
      // Ensure proper heights
      const card = item.querySelector('.dashboard-card');
      if (card) {
        card.style.height = '100%';
      }
    });
  }

  /**
   * Handle content loaded event
   */
  handleContentLoaded () {
    // Refresh grid layout after all content has loaded
    if (this.grid) {
      setTimeout(() => {
        this.optimizeGridLayout();
      }, 100);
    }
  }

  /**
   * Handle window resize event
   */
  handleResize () {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      if (this.grid) {
        this.optimizeGridLayout();
      }
    }, 200);
  }

  /**
   * Persist the tile order after a drag. Muuri fires dragReleaseEnd when
   * the dropped tile settles; we read the grid's current visual order
   * (tiles carry data-tile-key) and save it debounced. Server keeps the
   * enabled flags untouched on order-only saves.
   */
  initOrderPersistence () {
    if (!this.grid) return;

    this.grid.on('dragReleaseEnd', () => {
      clearTimeout(this.orderSaveTimeout);
      this.orderSaveTimeout = setTimeout(() => this.saveOrder(), 600);
    });
  }

  saveOrder () {
    if (!this.grid) return;
    const order = this.grid.getItems()
      .map(item => item.getElement().dataset.tileKey)
      .filter(Boolean);
    if (!order.length) return; // tiles without keys — nothing to persist

    fetch(Dashboard.PREFS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ order }),
    }).catch(() => { /* non-fatal — order just won't stick this time */ });
  }

  /**
   * Customize panel: toggle tiles on/off (checkbox list is
   * server-rendered from the user's permission-visible tiles), save →
   * reload; reset → wipe prefs → reload.
   */
  initCustomize () {
    const btn = document.getElementById('dash-customize-btn');
    const panel = document.getElementById('dash-customize-panel');
    if (!btn || !panel) return;

    btn.addEventListener('click', () => {
      panel.hidden = !panel.hidden;
    });

    const status = document.getElementById('dash-customize-status');
    const say = (msg) => { if (status) status.textContent = msg; };

    const saveBtn = document.getElementById('dash-customize-save');
    const resetBtn = document.getElementById('dash-customize-reset');
    const setBusy = (busy) => {
      if (saveBtn) saveBtn.disabled = busy;
      if (resetBtn) resetBtn.disabled = busy;
    };

    if (saveBtn) {
      saveBtn.addEventListener('click', () => {
        // A pending debounced drag-save racing this POST can dup-key or
        // partially overwrite — cancel it; this save carries the order.
        clearTimeout(this.orderSaveTimeout);

        // Keep the user's current on-screen order for enabled tiles:
        // ordered keys from the grid first, then newly-enabled ones.
        const gridOrder = this.grid
          ? this.grid.getItems().map(i => i.getElement().dataset.tileKey).filter(Boolean)
          : [];
        const boxes = Array.from(panel.querySelectorAll('input[data-tile-key]'));
        const rank = (key) => {
          const i = gridOrder.indexOf(key);
          return i === -1 ? gridOrder.length + boxes.findIndex(b => b.dataset.tileKey === key) : i;
        };
        const tiles = boxes
          .map(b => ({ key: b.dataset.tileKey, enabled: b.checked }))
          .sort((a, b) => rank(a.key) - rank(b.key))
          .map((t, i) => ({ ...t, sort_order: i }));

        say('Saving…');
        setBusy(true);
        fetch(Dashboard.PREFS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ tiles }),
        })
          .then(r => { if (!r.ok) throw new Error(); location.reload(); })
          .catch(() => { say('Save failed — try again.'); setBusy(false); });
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        // Cancel any pending drag-save: it would fire before the reload
        // and silently re-insert the order Reset just wiped.
        clearTimeout(this.orderSaveTimeout);

        say('Resetting…');
        setBusy(true);
        fetch(Dashboard.PREFS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ reset: true }),
        })
          .then(r => { if (!r.ok) throw new Error(); location.reload(); })
          .catch(() => { say('Reset failed — try again.'); setBusy(false); });
      });
    }
  }
}

// Initialize dashboard when DOM is ready
;(function() {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector(Dashboard.EL_CONTAINER) !== null) {
      window.sflow.dashboard = new Dashboard();
    }
  });
})();
